import { Minus, Plus, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CART_NOTE,
  buildOrderWhatsAppUrl,
  formatRM,
  scrollToSection,
} from "@/data/products";
import ProductImage from "@/components/ProductImage";

export default function CartDrawer({ open, onOpenChange, cart }) {
  const { lines, setQty, remove, subtotal } = cart;
  const empty = lines.length === 0;

  const browse = () => {
    onOpenChange(false);
    scrollToSection("products");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col bg-cream sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-xl text-charcoal">Your Cart</SheetTitle>
        </SheetHeader>

        {empty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <p className="text-sm text-charcoal/65" data-testid="cart-empty-message">
              Your cart is empty. Browse the catalogue and add products to send
              an order enquiry.
            </p>
            <button
              type="button"
              onClick={browse}
              className="rounded-full bg-forest px-6 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-forest-mid"
              data-testid="cart-browse-products"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <>
            <ul className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1" data-testid="cart-items">
              {lines.map((line) => (
                <li
                  key={line.id}
                  className="flex gap-3 rounded-xl border border-charcoal/10 bg-white p-3"
                  data-testid={`cart-item-${line.id}`}
                >
                  <ProductImage
                    product={line}
                    compact
                    className="h-20 w-20 shrink-0 rounded-lg border border-charcoal/10"
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate text-sm font-semibold text-charcoal" title={line.name}>
                      {line.name}
                    </p>
                    <p className="mt-0.5 text-xs text-charcoal/60">
                      {formatRM(line.price)} each · {line.condition}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQty(line.id, line.qty - 1)}
                        className="rounded-full border border-charcoal/20 p-1.5 text-charcoal transition-colors hover:border-forest hover:text-forest"
                        aria-label={`Decrease quantity of ${line.name}`}
                        data-testid={`qty-decrease-${line.id}`}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={line.qty}
                        onChange={(e) => setQty(line.id, e.target.value)}
                        className="w-14 rounded-lg border border-charcoal/20 px-2 py-1 text-center text-sm"
                        aria-label={`Quantity of ${line.name}`}
                        data-testid={`qty-input-${line.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => setQty(line.id, line.qty + 1)}
                        className="rounded-full border border-charcoal/20 p-1.5 text-charcoal transition-colors hover:border-forest hover:text-forest"
                        aria-label={`Increase quantity of ${line.name}`}
                        data-testid={`qty-increase-${line.id}`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(line.id)}
                        className="ml-auto rounded-full p-1.5 text-charcoal/50 transition-colors hover:text-red-700"
                        aria-label={`Remove ${line.name} from cart`}
                        data-testid={`remove-item-${line.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-2 text-sm font-bold text-forest" data-testid={`line-total-${line.id}`}>
                      {formatRM(line.price * line.qty)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-4 border-t border-charcoal/15 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-charcoal">Merchandise subtotal</span>
                <span className="text-lg font-bold text-forest" data-testid="cart-subtotal">
                  {formatRM(subtotal)}
                </span>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-charcoal/60" data-testid="cart-note">
                {CART_NOTE}
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <a
                  href={buildOrderWhatsAppUrl(lines, subtotal)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-[#1fa855] px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-[#178a45]"
                  data-testid="send-order-whatsapp"
                >
                  Send Order via WhatsApp
                </a>
                <button
                  type="button"
                  onClick={browse}
                  className="rounded-full border border-charcoal/25 px-6 py-2.5 text-sm font-semibold text-charcoal transition-colors hover:border-forest hover:text-forest"
                  data-testid="cart-continue-shopping"
                >
                  Continue shopping
                </button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
